import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getDashboardStats,
} from "../../features/admin/adminSlice";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_URL as BASE_API_URL } from "../../config/apiConfig";
import {
  FaUser,
  FaCalendarAlt,
  FaRupeeSign,
  FaFilter,
  FaSearch,
  FaTaxi,
  FaUserTie,
  FaListAlt,
  FaBuilding,
} from "react-icons/fa";
import { formatDateDDMMYYYY } from "../../utils/dateUtils";

const getShortAddress = (address) => {
  if (!address || typeof address !== 'string') return "N/A";
  
  let cleanAddress = address.replace(/\s+/g, ' ').trim();
  
  const parts = cleanAddress.split(',')
    .map(p => p.trim())
    .filter(p => p && !p.includes('+'));
  
  if (parts.length === 0) return address;
  if (cleanAddress.length <= 35) return cleanAddress;

  let streetPart = parts[0];
  let nextIdx = 1;
  if ((/^\d+$/.test(streetPart) || streetPart.length <= 4) && parts.length > 1) {
    streetPart = `${parts[0]}, ${parts[1]}`;
    nextIdx = 2;
  }
  
  let cityIdx = parts.length - 2;
  if (cityIdx >= 0) {
    const lastPartLower = parts[parts.length - 1].toLowerCase();
    if (lastPartLower === "india" && parts.length >= 3) {
      cityIdx = parts.length - 3;
    }
  }
  
  const cityPart = cityIdx >= nextIdx ? parts[cityIdx] : "";
  
  if (cityPart && streetPart.toLowerCase().includes(cityPart.toLowerCase())) {
    return streetPart;
  }
  
  return cityPart ? `${streetPart}, ${cityPart}` : streetPart;
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const { dashboardStats, isLoading } =
    useSelector((state) => state.admin);
  const { user } = useSelector((state) => state.auth);

  const [bookingFilter, setBookingFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Prevent multiple API calls with a ref
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current && !isLoading) {
      hasInitialized.current = true;
      dispatch(getDashboardStats());
    }
  }, [dispatch, isLoading, user]);

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-[50vh] text-slate-400 gap-3">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
        <span className="text-sm font-semibold">Loading dashboard data...</span>
      </div>
    );
  }

  // If stats are not yet loaded, show placeholders
  const stats = dashboardStats || {
    totalUsers: 0,
    totalBookings: 0,
    totalCabs: 0,
    recentBookings: [],
    monthlyRevenueChart: [],
    totalRevenue: 0,
    pendingBookings: 0,
    completedBookings: 0,
    cancelledBookings: 0,
    todayBookings: 0,
    weeklyRevenue: 0,
    monthlyRevenue: 0,
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "cancelled":
        return "bg-red-50 text-red-700 border-red-200";
      case "pending":
        return "bg-amber-50 text-amber-700 border-amber-200";
      default:
        return "bg-blue-50 text-blue-700 border-blue-200";
    }
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
      {/* Row 1: Top summary directory cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        <Link to="/admin/users" className="block group">
          <div className="bg-white rounded-2xl shadow-sm hover:shadow-md p-6 border border-slate-100 transition-all duration-200 hover:-translate-y-1 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3.5 rounded-xl bg-blue-50 text-blue-600 group-hover:scale-105 transition-transform duration-200">
                <FaUser className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Total Users</span>
                <span className="text-2xl font-black text-slate-800 mt-1 block">{stats.totalUsers || 0}</span>
              </div>
            </div>
            <span className="text-xs text-blue-500 font-bold group-hover:translate-x-0.5 transition-transform duration-200">View All →</span>
          </div>
        </Link>

        <Link to="/admin/drivers" className="block group">
          <div className="bg-white rounded-2xl shadow-sm hover:shadow-md p-6 border border-slate-100 transition-all duration-200 hover:-translate-y-1 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3.5 rounded-xl bg-purple-50 text-purple-600 group-hover:scale-105 transition-transform duration-200">
                <FaUserTie className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Total Drivers</span>
                <span className="text-2xl font-black text-slate-800 mt-1 block">{stats.totalDrivers || 0}</span>
              </div>
            </div>
            <span className="text-xs text-purple-500 font-bold group-hover:translate-x-0.5 transition-transform duration-200">View All →</span>
          </div>
        </Link>

        <Link to="/admin/vendors" className="block group">
          <div className="bg-white rounded-2xl shadow-sm hover:shadow-md p-6 border border-slate-100 transition-all duration-200 hover:-translate-y-1 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3.5 rounded-xl bg-emerald-50 text-emerald-600 group-hover:scale-105 transition-transform duration-200">
                <FaBuilding className="w-5 h-5" />
              </div>
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Total Vendors</span>
                <span className="text-2xl font-black text-slate-800 mt-1 block">{stats.totalVendors || 0}</span>
              </div>
            </div>
            <span className="text-xs text-emerald-500 font-bold group-hover:translate-x-0.5 transition-transform duration-200">View All →</span>
          </div>
        </Link>
      </div>

      {/* Row 2: Booking status overview grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        <Link to="/admin/bookings?status=pending" className="block group">
          <div className="bg-white rounded-2xl shadow-sm hover:shadow-md p-6 border border-slate-100 transition-all duration-200 hover:-translate-y-1 flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Pending Bookings</span>
              <span className="text-2xl font-black text-slate-800 mt-1 block">{stats.pendingBookings || 0}</span>
            </div>
            <div className="p-3.5 rounded-xl bg-amber-50 text-amber-600 group-hover:scale-105 transition-transform duration-200">
              <FaCalendarAlt className="w-5 h-5" />
            </div>
          </div>
        </Link>

        <Link to="/admin/bookings?status=assigned" className="block group">
          <div className="bg-white rounded-2xl shadow-sm hover:shadow-md p-6 border border-slate-100 transition-all duration-200 hover:-translate-y-1 flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Assigned To Driver</span>
              <span className="text-2xl font-black text-slate-800 mt-1 block">{stats.assignedToDriver || 0}</span>
            </div>
            <div className="p-3.5 rounded-xl bg-indigo-50 text-indigo-600 group-hover:scale-105 transition-transform duration-200">
              <FaTaxi className="w-5 h-5" />
            </div>
          </div>
        </Link>

        <Link to="/admin/bookings?status=assigned_to_vendor" className="block group">
          <div className="bg-white rounded-2xl shadow-sm hover:shadow-md p-6 border border-slate-100 transition-all duration-200 hover:-translate-y-1 flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Assigned To Vendor</span>
              <span className="text-2xl font-black text-slate-800 mt-1 block">
                {stats.assignedToVendor || 0}
              </span>
            </div>
            <div className="p-3.5 rounded-xl bg-red-50 text-red-600 group-hover:scale-105 transition-transform duration-200">
              <FaListAlt className="w-5 h-5" />
            </div>
          </div>
        </Link>
      </div>

      {/* Row 3: Earnings cards with custom premium gradients */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 text-white rounded-2xl shadow-lg shadow-emerald-500/10 p-6 flex flex-col justify-between">
          <span className="text-[10px] font-black uppercase tracking-widest text-emerald-100">Today's Earnings</span>
          <div className="flex items-center gap-1 mt-4">
            <FaRupeeSign className="text-xl text-emerald-100" />
            <span className="text-3xl font-black">{stats.todayAdminEarnings || 0}</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 text-white rounded-2xl shadow-lg shadow-blue-500/10 p-6 flex flex-col justify-between">
          <span className="text-[10px] font-black uppercase tracking-widest text-blue-100">Weekly Earnings</span>
          <div className="flex items-center gap-1 mt-4">
            <FaRupeeSign className="text-xl text-blue-100" />
            <span className="text-3xl font-black">{stats.weeklyAdminEarnings || 0}</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 via-purple-600 to-pink-600 text-white rounded-2xl shadow-lg shadow-purple-500/10 p-6 flex flex-col justify-between">
          <span className="text-[10px] font-black uppercase tracking-widest text-purple-100">Total Earnings</span>
          <div className="flex items-center gap-1 mt-4">
            <FaRupeeSign className="text-xl text-purple-100" />
            <span className="text-3xl font-black">{stats.totalAdminEarnings || 0}</span>
          </div>
        </div>
      </div>

      {/* Booking list card */}
      <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 mb-8 border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <FaListAlt className="text-blue-500" /> Recent Bookings
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">Filter and search the latest system booking logs.</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search Input */}
            <div className="relative border border-slate-200 focus-within:border-blue-500 rounded-xl transition-all duration-200 bg-slate-50/50">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                <FaSearch />
              </div>
              <input
                type="text"
                placeholder="Search logs..."
                className="pl-10 pr-4 py-2 bg-transparent text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none w-full sm:w-56"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Filter Select */}
            <div className="relative border border-slate-200 focus-within:border-blue-500 rounded-xl transition-all duration-200 bg-slate-50/50">
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                <FaFilter />
              </div>
              <select
                className="pl-10 pr-8 py-2 bg-transparent text-xs font-bold text-slate-600 focus:outline-none appearance-none cursor-pointer rounded-xl"
                value={bookingFilter}
                onChange={(e) => setBookingFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Directory Table */}
        <div className="overflow-x-auto -mx-6 sm:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-slate-100">
              <thead>
                <tr className="bg-slate-50/70">
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider rounded-l-xl">
                    Booking ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                    User Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                    Route
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider rounded-r-xl">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {stats.recentBookings && stats.recentBookings.length > 0 ? (
                  stats.recentBookings
                    .filter((booking) => {
                      if (bookingFilter !== "all" && booking.status !== bookingFilter) {
                        return false;
                      }

                      if (searchQuery) {
                        const searchLower = searchQuery.toLowerCase();
                        const idMatch = booking._id && booking._id.toLowerCase().includes(searchLower);
                        const bookingIdMatch = booking.bookingId && booking.bookingId.toLowerCase().includes(searchLower);
                        const userMatch = booking.user?.name && booking.user.name.toLowerCase().includes(searchLower);
                        const userNameMatch = booking.userName && booking.userName.toLowerCase().includes(searchLower);
                        const fromMatch = (booking.from || booking.pickupLocation || booking.fromCity || "").toLowerCase().includes(searchLower);
                        const toMatch = (booking.to || booking.dropLocation || booking.toCity || "").toLowerCase().includes(searchLower);
                        const phoneMatch = (booking.user?.phone || booking.userPhone || booking.passengerDetails?.phone || "").toString().toLowerCase().includes(searchLower);
                        const serviceMatch = (booking.serviceType || booking.journeyType || "").toString().toLowerCase().includes(searchLower);

                        return idMatch || bookingIdMatch || userMatch || userNameMatch || phoneMatch || serviceMatch || fromMatch || toMatch;
                      }

                      return true;
                    })
                    .map((booking) => {
                      const displayIdRaw = booking.bookingId || (booking._id ? booking._id.slice(-6).toUpperCase() : "N/A");
                      const displayId = displayIdRaw && typeof displayIdRaw === 'string' && displayIdRaw.startsWith('#') ? displayIdRaw : `#${displayIdRaw}`;
                      const displayDate = booking.date
                        ? new Date(booking.date)
                        : booking.pickupDate
                          ? new Date(booking.pickupDate)
                          : booking.travelDate
                            ? new Date(booking.travelDate)
                            : null;
                      const displayTime = booking.pickupTime || (displayDate ? displayDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "");
                      const serviceRaw = booking.serviceType || booking.journeyType || "";
                      const serviceLabel = typeof serviceRaw === 'string' ? serviceRaw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '';
                      const phone = booking.user?.phone || booking.userPhone || booking.passengerDetails?.phone || '';

                      return (
                        <tr key={booking._id || booking.bookingId} className="hover:bg-slate-50/40 transition-colors duration-150">
                          <td className="px-4 py-4 whitespace-nowrap text-xs font-black text-blue-600">
                            {displayId}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="text-xs font-bold text-slate-800">
                                {booking.user?.name || booking.userName || "Guest"}
                              </span>
                              {phone && (
                                <span className="text-[10px] text-slate-400 font-semibold mt-0.5">
                                  {phone}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className="text-xs font-semibold text-slate-700" title={booking.from || booking.pickupLocation || booking.fromCity || "N/A"}>
                                {getShortAddress(booking.from || booking.pickupLocation || booking.fromCity || "N/A")}
                              </span>
                              <span className="text-[10px] text-slate-400 font-bold mt-0.5" title={booking.to || booking.dropLocation || booking.toCity || "N/A"}>
                                → {getShortAddress(booking.to || booking.dropLocation || booking.toCity || "N/A")}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-xs font-semibold text-slate-500">
                            <div className="flex flex-col">
                              <span>{displayDate ? formatDateDDMMYYYY(displayDate) : "N/A"}</span>
                              {displayTime && (
                                <span className="text-[10px] text-slate-400 font-bold mt-0.5">
                                  {displayTime}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-xs font-bold text-slate-500">
                            {serviceLabel}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${getStatusBadge(booking.status)}`}>
                              {booking.status?.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="text-xs font-black text-emerald-600">
                              ₹{booking.amount || booking.totalAmount || booking.fare || 0}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                ) : searchQuery || bookingFilter !== "all" ? (
                  <tr>
                    <td className="py-8 px-4 text-center text-xs font-bold text-slate-400" colSpan="7">
                      No bookings match your filters (Filter: {bookingFilter}, Search: "{searchQuery}")
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td className="py-8 px-4 text-center text-xs font-bold text-slate-400" colSpan="7">
                      No recent bookings available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
